// ============================================================
// VCS.SfuClient — mediasoup-client Wrapper fuer Group Video Calls
// ============================================================
// Kapselt mediasoup Device, Transports, Producer und Consumer.
// Kommuniziert mit dem Server ueber VCS.signaling?.send().
//
// Voraussetzung: window.mediasoupClient (UMD-Bundle) geladen.
// ============================================================
window.VCS = window.VCS || {};

VCS.SfuClient = class {

    constructor() {
        this._device = null;
        this._sendTransport = null;
        this._recvTransport = null;
        this._producers = new Map();   // kind ('audio'|'video') -> mediasoup Producer
        this._consumers = new Map();   // consumerId -> { consumer, clientId, kind }

        // Pending-Promises fuer Request/Response-Paare mit dem Server
        this._pending = new Map();     // requestId -> { resolve, reject }
        this._reqId = 0;
    }

    // ── Setup nach group-call-joined ────────────────────────

    async setup() {
        if (this._setupRunning) return;
        this._setupRunning = true;

        if (!window.mediasoupClient) {
            console.error('[sfu] mediasoup-client nicht geladen');
            this._setupRunning = false;
            return;
        }

        // 1. Router-Capabilities vom Server holen
        const caps = await this._request('sfu-get-capabilities');
        if (!caps?.rtpCapabilities) {
            console.error('[sfu] Keine RTP-Capabilities vom Server');
            this._setupRunning = false;
            return;
        }

        // 2. Device erstellen und laden
        this._device = new mediasoupClient.Device();
        await this._device.load({ routerRtpCapabilities: caps.rtpCapabilities });
        console.log('[sfu] Device geladen, canProduce: audio=%s video=%s',
            this._device.canProduce('audio'), this._device.canProduce('video'));

        // 3. Eigene Capabilities an Server melden
        VCS.signaling?.send({
            type           : 'sfu-set-rtp-capabilities',
            rtpCapabilities: this._device.rtpCapabilities,
        });

        // 4. Send-Transport erstellen
        await this._createSendTransport();

        // 5. Recv-Transport erstellen
        await this._createRecvTransport();

        // 6. Eigene Tracks publishen
        await this._produceLocalTracks();

        // 7. Bestehende Producer im Room abfragen und konsumieren
        const existing = await this._request('sfu-get-producers');
        if (existing?.producers) {
            for (const p of existing.producers) {
                await this.consumeProducer(p.producerId, p.clientId, p.kind);
            }
        }

        this._setupRunning = false;
        console.log('[sfu] Setup komplett');
    }

    // ── Send-Transport ──────────────────────────────────────

    async _createSendTransport() {
        const data = await this._request('sfu-create-transport', { direction: 'send' });

        this._sendTransport = this._device.createSendTransport({
            id            : data.id,
            iceParameters : data.iceParameters,
            iceCandidates : data.iceCandidates,
            dtlsParameters: data.dtlsParameters,
            iceServers    : VCS.Signaling.ICE_SERVERS,
            iceTransportPolicy: 'all',
        });

        // Server-Verbindung wenn Transport ICE/DTLS starten will
        this._sendTransport.on('connect', ({ dtlsParameters }, callback, errback) => {
            VCS.signaling?.send({
                type          : 'sfu-connect-transport',
                transportId   : this._sendTransport.id,
                dtlsParameters,
            });
            // Wir warten auf sfu-transport-connected
            this._waitForEvent('sfu-transport-connected', this._sendTransport.id)
                .then(callback)
                .catch(errback);
        });

        // Server-seitigen Producer erstellen wenn produce() aufgerufen wird
        this._sendTransport.on('produce', ({ kind, rtpParameters, appData }, callback, errback) => {
            this._request('sfu-produce', { kind, rtpParameters, appData })
                .then(res => callback({ id: res.producerId }))
                .catch(errback);
        });

        console.log('[sfu] SendTransport erstellt: %s', this._sendTransport.id);
    }

    // ── Recv-Transport ──────────────────────────────────────

    async _createRecvTransport() {
        const data = await this._request('sfu-create-transport', { direction: 'recv' });

        this._recvTransport = this._device.createRecvTransport({
            id            : data.id,
            iceParameters : data.iceParameters,
            iceCandidates : data.iceCandidates,
            dtlsParameters: data.dtlsParameters,
            iceServers    : VCS.Signaling.ICE_SERVERS,
            iceTransportPolicy: 'all',
        });

        this._recvTransport.on('connect', ({ dtlsParameters }, callback, errback) => {
            VCS.signaling?.send({
                type          : 'sfu-connect-transport',
                transportId   : this._recvTransport.id,
                dtlsParameters,
            });
            this._waitForEvent('sfu-transport-connected', this._recvTransport.id)
                .then(callback)
                .catch(errback);
        });

        console.log('[sfu] RecvTransport erstellt: %s', this._recvTransport.id);
    }

    // ── Lokale Tracks publizieren ───────────────────────────

    async _produceLocalTracks() {
        const s = VCS.state;
        if (!s.localStream || !this._sendTransport) return;

        // Audio
        const audioTrack = s.localStream.getAudioTracks()[0];
        if (audioTrack && this._device.canProduce('audio')) {
            try {
                const producer = await this._sendTransport.produce({ track: audioTrack });
                this._producers.set('audio', producer);
                producer.on('transportclose', () => this._producers.delete('audio'));
                if (s.audioMuted) producer.pause();
                console.log('[sfu] Audio-Producer erstellt: %s', producer.id);
            } catch (e) {
                console.error('[sfu] Audio-Produce Fehler:', e.message);
            }
        }

        // Video
        const videoTrack = s.localStream.getVideoTracks()[0];
        if (videoTrack && this._device.canProduce('video')) {
            try {
                const producer = await this._sendTransport.produce({
                    track    : videoTrack,
                    encodings: [
                        { maxBitrate: 100000, scaleResolutionDownBy: 4 },
                        { maxBitrate: 300000, scaleResolutionDownBy: 2 },
                        { maxBitrate: 800000 },
                    ],
                    codecOptions: {
                        videoGoogleStartBitrate: 500,
                    },
                });
                this._producers.set('video', producer);
                producer.on('transportclose', () => this._producers.delete('video'));
                if (s.videoOff) producer.pause();
                console.log('[sfu] Video-Producer erstellt: %s (Simulcast 3 Layer)', producer.id);
            } catch (e) {
                console.error('[sfu] Video-Produce Fehler:', e.message);
            }
        }
    }

    // ── Remote-Producer konsumieren ─────────────────────────

    async consumeProducer(producerId, producerClientId, kind) {
        if (!this._recvTransport || !this._device) return null;

        try {
            const data = await this._request('sfu-consume', {
                producerId,
                producerClientId,
            });

            const consumer = await this._recvTransport.consume({
                id           : data.consumerId,
                producerId   : data.producerId,
                kind         : data.kind,
                rtpParameters: data.rtpParameters,
            });

            this._consumers.set(consumer.id, {
                consumer,
                clientId: producerClientId,
                kind    : data.kind,
            });

            consumer.on('transportclose', () => this._consumers.delete(consumer.id));

            // Server mitteilen, dass der Consumer bereit ist
            VCS.signaling?.send({
                type      : 'sfu-resume-consumer',
                consumerId: consumer.id,
            });

            // Remote-Stream dem Peer zuweisen
            this._assignConsumerToGrid(producerClientId, consumer);

            console.log('[sfu] Consumer erstellt: peer=%s kind=%s consumerId=%s',
                producerClientId, data.kind, consumer.id);
            return consumer;
        } catch (e) {
            console.error('[sfu] Consume Fehler:', e.message);
            return null;
        }
    }

    // ── Consumer-Track zuweisen (Group-Grid oder 1:1-Video) ──

    _assignConsumerToGrid(clientId, consumer) {
        const s = VCS.state;

        // 1:1-Fallback: Track direkt ins Remote-Video-Element
        if (s._sfuFallbackActive && !s.groupCallActive) {
            if (!s.remoteStream) {
                s.remoteStream = new MediaStream();
            }
            const oldTracks = s.remoteStream.getTracks().filter(t => t.kind === consumer.kind);
            oldTracks.forEach(t => s.remoteStream.removeTrack(t));
            s.remoteStream.addTrack(consumer.track);

            const remoteVid = document.getElementById('remoteVideo');
            if (remoteVid) {
                remoteVid.srcObject = s.remoteStream;
                remoteVid.play().catch(() => {});
            }
            if (consumer.kind === 'audio') {
                VCS.audioMonitor?.startRemote(s.remoteStream);
            }
            console.log('[sfu] 1:1-Fallback: Track zugewiesen kind=' + consumer.kind);
            return;
        }

        // Group-Call: Track ins Grid
        const peerEntry = s.groupCallPeers.get(clientId);
        if (!peerEntry) return;

        if (!peerEntry.remoteStream) {
            peerEntry.remoteStream = new MediaStream();
        }

        const oldTracks = peerEntry.remoteStream.getTracks().filter(t => t.kind === consumer.kind);
        oldTracks.forEach(t => peerEntry.remoteStream.removeTrack(t));
        peerEntry.remoteStream.addTrack(consumer.track);

        VCS.webrtc?.renderGroupGrid();
    }

    // ── Producer bei geschlossenem Remote-Producer entfernen ─

    removeConsumerByProducerClose(consumerId) {
        const entry = this._consumers.get(consumerId);
        if (!entry) return;

        const { consumer, clientId, kind } = entry;

        // Track aus dem Peer-Stream entfernen
        const peerEntry = VCS.state.groupCallPeers.get(clientId);
        if (peerEntry?.remoteStream) {
            const tracks = peerEntry.remoteStream.getTracks().filter(t => t.id === consumer.track?.id);
            tracks.forEach(t => peerEntry.remoteStream.removeTrack(t));
        }

        consumer.close();
        this._consumers.delete(consumerId);
        VCS.webrtc?.renderGroupGrid();

        console.log('[sfu] Consumer entfernt (producer-closed): peer=%s kind=%s', clientId, kind);
    }

    // ── Mute / Unmute ───────────────────────────────────────

    pauseProducer(kind) {
        const producer = this._producers.get(kind);
        if (producer && !producer.paused) {
            producer.pause();
            VCS.signaling?.send({ type: 'sfu-pause-producer', kind });
        }
    }

    resumeProducer(kind) {
        const producer = this._producers.get(kind);
        if (producer && producer.paused) {
            producer.resume();
            VCS.signaling?.send({ type: 'sfu-resume-producer', kind });
        }
    }

    // ── Video-Track ersetzen (Cam on/off, Blur wechsel) ─────

    async replaceVideoTrack(newTrack) {
        const producer = this._producers.get('video');
        if (producer && newTrack) {
            await producer.replaceTrack({ track: newTrack });
            console.log('[sfu] Video-Track ersetzt');
        }
    }

    // ── Cleanup ─────────────────────────────────────────────

    close() {
        // Consumer schliessen
        for (const [, entry] of this._consumers) {
            entry.consumer.close();
        }
        this._consumers.clear();

        // Producer schliessen
        for (const [, producer] of this._producers) {
            producer.close();
        }
        this._producers.clear();

        // Transports schliessen
        if (this._sendTransport) {
            this._sendTransport.close();
            this._sendTransport = null;
        }
        if (this._recvTransport) {
            this._recvTransport.close();
            this._recvTransport = null;
        }

        this._device = null;
        this._pending.clear();

        console.log('[sfu] Closed');
    }

    // ── Server-Kommunikation (Request/Response) ─────────────

    // Sendet WS-Nachricht und wartet auf passende Antwort
    _request(type, data = {}) {
        return new Promise((resolve, reject) => {
            const reqId = ++this._reqId;
            const timeout = setTimeout(() => {
                this._pending.delete(reqId);
                reject(new Error('SFU request timeout: ' + type));
            }, 10000);

            this._pending.set(reqId, { resolve, reject, timeout, responseType: this._responseType(type) });
            VCS.signaling?.send({ ...data, type, _sfuReqId: reqId });
        });
    }

    // Mapping: Request-Type -> erwarteter Response-Type
    _responseType(reqType) {
        const map = {
            'sfu-get-capabilities' : 'sfu-capabilities',
            'sfu-create-transport' : 'sfu-transport-created',
            'sfu-produce'          : 'sfu-produced',
            'sfu-consume'          : 'sfu-consumed',
            'sfu-get-producers'    : 'sfu-existing-producers',
        };
        return map[reqType] || null;
    }

    // Wird von signaling.js aufgerufen wenn SFU-Response kommt
    handleResponse(msg) {
        // Finde passendes Pending per _sfuReqId
        if (msg._sfuReqId && this._pending.has(msg._sfuReqId)) {
            const p = this._pending.get(msg._sfuReqId);
            clearTimeout(p.timeout);
            this._pending.delete(msg._sfuReqId);
            p.resolve(msg);
            return true;
        }

        // Fallback: matche per Response-Type (fuer Faelle ohne reqId)
        for (const [reqId, p] of this._pending) {
            if (p.responseType === msg.type) {
                clearTimeout(p.timeout);
                this._pending.delete(reqId);
                p.resolve(msg);
                return true;
            }
        }
        return false;
    }

    // Wartet auf ein spezifisches Event (z.B. sfu-transport-connected)
    _waitForEvent(eventType, matchId) {
        return new Promise((resolve, reject) => {
            const key = eventType + ':' + matchId;
            const timeout = setTimeout(() => {
                this._pending.delete(key);
                reject(new Error('SFU event timeout: ' + key));
            }, 10000);

            this._pending.set(key, { resolve, reject, timeout, responseType: null });
        });
    }

    // Wird von signaling.js aufgerufen fuer sfu-transport-connected
    handleTransportConnected(msg) {
        const key = 'sfu-transport-connected:' + msg.transportId;
        const p = this._pending.get(key);
        if (p) {
            clearTimeout(p.timeout);
            this._pending.delete(key);
            p.resolve();
        }
    }
};
